<template>
  <div class="tw-flex tw-gap-x-5">
    <filter-menu
      v-if="Settings.allowFiltering"
      :id="FOLLOWED_COLLECTIONS_ID"
      :show="filterMenuOpen"
      :sort-options="LIBRARY_COLLECTION_SORT_OPTIONS"
      @update="updateTable"
    />
    <div
      v-if="subscribedCollections.data.length < 1 && !loadingFollowed"
      class="tw-flex-1"
    >
      <no-result
        class="tw-w-full"
        item-name="collection"
        :is-filtering="hasAnyFilters"
        @click="resetAllFilters"
      />
    </div>
    <div
      v-else
      :class="
        Settings.allowFiltering && filterMenuOpen
          ? 'md:tw-w-[calc(100%-17.25rem-1px)]'
          : 'tw-w-full'
      "
    >
      <modio-table
        ref="libraryCollectionsTable"
        :table-id="FOLLOWED_COLLECTIONS_ID"
        :class="Classes.LIBRARY"
        :row-schema="schema"
        :items="subscribedCollections"
        :item-total="subscribedCollections.data.length"
        :fetch="false"
        :limit="50"
        :loading="loadingFollowed"
        :error="errorFollowed"
        :no-data-text="
          $t('no_subscriptions', { ugc_name: $t('collection', 2) })
        "
      />
    </div>
  </div>

  <base-modal
    v-if="Settings.allowFiltering && filterMenuOpen"
    show
    no-margin
    no-padding
    bottom
    has-close-btn
    :close-on-escape="false"
    class="sm:tw-hidden"
    @close:modal="toggleFilterMenu"
  >
    <filter-menu
      :id="FOLLOWED_COLLECTIONS_ID"
      show
      modal
      :sort-options="LIBRARY_COLLECTION_SORT_OPTIONS"
      @update="updateTable"
    />
  </base-modal>

  <teleport to="body">
    <collection-subscribe-modal
      v-if="selectedCollection"
      :show="isSubscribeModalOpen"
      :collection="selectedCollection"
      :is-subscribed="isSubscribedToSelected"
      @close:modal="hideSubscribeModal"
    />
  </teleport>
</template>

<script>
import CollectionSubscribeModal from '@components/Modals/CollectionSubscribeModal.vue'
import { useSearch, useRoute, useAsync, useModal, useSort } from '@composables'
import { watchEffect, onMounted, computed, provide, watch, ref } from 'vue'
import { LIBRARY_COLLECTION_SORT_OPTIONS } from '@config/sort.js'
import { schema } from '@config/schemas/myFollowedCollections.js'
import { collectionFollowingStore, gameStore } from '@stores'
import { getMyFollowedCollectionsRequest } from '@services'
import ModioTable from '@components/Table/ModioTable.vue'
import FilterMenu from '@components/List/FilterMenu.vue'
import NoResult from '@components/List/NoResult.vue'
import Classes from '@shared/js/config/css.js'
import { useBreakpoints } from '@vueuse/core'
import { Settings } from '@config/game.js'
import { BREAKPOINTS } from '@config'
import pick from 'lodash/pick'
import useFilter, {
  normalizeCollectionFilters,
  FOLLOWED_COLLECTIONS_ID,
  toggleFilterMenu,
  filterMenuOpen,
} from '@composables/useFilter.js'
export default {
  components: {
    CollectionSubscribeModal,
    FilterMenu,
    NoResult,
    ModioTable,
  },
  setup() {
    const followedCollectionList = ref([])
    const selectedCollection = ref(null)
    const libraryCollectionsTable = ref(null)

    const { getPublicGame } = gameStore()
    const { setMyFollowedCollections, followedCollections } =
      collectionFollowingStore()

    const { routeQuery } = useRoute()
    const { greaterOrEqual } = useBreakpoints(BREAKPOINTS)
    const smAndUp = greaterOrEqual('sm')
    const game = getPublicGame()

    const {
      activeFiltersCount,
      inMemoryFilter,
      activeFilters,
      resetFilter,
      setFilter,
      filter,
    } = useFilter(FOLLOWED_COLLECTIONS_ID)

    const {
      clearUserSearch,
      searchUserName,
      inMemorySearch,
      clearSearch,
      search,
    } = useSearch(FOLLOWED_COLLECTIONS_ID)

    const { setSort } = useSort(
      FOLLOWED_COLLECTIONS_ID,
      undefined,
      LIBRARY_COLLECTION_SORT_OPTIONS
    )

    const defaultSort = LIBRARY_COLLECTION_SORT_OPTIONS.find((x) => x.default)

    setSort({
      sortBy: defaultSort.key,
      order: defaultSort.direction,
      direction: defaultSort.direction,
    })

    const hasAnyFilters = computed(
      () =>
        !!activeFiltersCount.value || !!search.value || !!searchUserName.value
    )

    watch(smAndUp, (_smAndUp) => {
      if (Settings.value.allowFiltering && _smAndUp !== filterMenuOpen.value) {
        toggleFilterMenu()
      }
    })

    const {
      isModalOpen: isSubscribeModalOpen,
      showModal: showSubscribeModal,
      hideModal: hideSubscribeModal,
    } = useModal()

    const {
      loading: loadingFollowed,
      error: errorFollowed,
      data: dataFollowed,
      run: runFollowed,
    } = useAsync(getMyFollowedCollectionsRequest)

    watchEffect(() => {
      // Add new subscriptions to the table if they come from somewhere else
      followedCollections.value?.forEach((s) => {
        if (followedCollectionList.value.every((m) => m.id !== s.id)) {
          followedCollectionList.value.push(s)
        }
      })
    })

    const subscribedCollections = computed(() => {
      let data = [...followedCollectionList.value]
      data = inMemorySearch(data)
      data = inMemoryFilter(data)

      return {
        data: data.map((c) => ({
          ...c,
          unfollowed: followedCollections.value?.every((s) => s.id !== c.id),
        })),
      }
    })

    const isSubscribedToSelected = computed(() => {
      return followedCollections.value.some(
        (subscription) =>
          subscription?.id === parseInt(selectedCollection.value?.id)
      )
    })

    function setupFilters() {
      const queryTags = [routeQuery.value.tags].filter((tag) => tag).join(',')
      const picks = ['tags', 'tags-not-in', 'date_live']

      if (!filter.value?.length || queryTags.length) {
        setFilter(
          normalizeCollectionFilters(
            game.value.collection_tag_options,
            pick(routeQuery.value, picks),

            false
          ),
          !queryTags
        )
      }
    }

    function resetAllFilters() {
      resetFilter()
      clearSearch()
      clearUserSearch()
    }

    function updateTable(_type, option) {
      if (_type === 'sort') {
        setSort({
          sortBy: option.key,
          order: option.direction,
          direction: option.direction,
        })

        libraryCollectionsTable.value?.updateTable('sort', option)
      }
    }

    onMounted(async () => {
      setupFilters()

      await runFollowed()

      if (!errorFollowed.value) {
        setMyFollowedCollections(dataFollowed.value.data)
      }
    })

    async function toggleFollow(collection) {
      selectedCollection.value = collection
      showSubscribeModal()
    }
    provide('toggleFollow', toggleFollow)

    return {
      LIBRARY_COLLECTION_SORT_OPTIONS,
      FOLLOWED_COLLECTIONS_ID,
      libraryCollectionsTable,
      isSubscribedToSelected,
      subscribedCollections,
      isSubscribeModalOpen,
      selectedCollection,
      hideSubscribeModal,
      toggleFilterMenu,
      resetAllFilters,
      loadingFollowed,
      filterMenuOpen,
      hasAnyFilters,
      activeFilters,
      errorFollowed,
      updateTable,
      Settings,
      Classes,
      schema,
    }
  },
}
</script>
